import React from 'react';

const MigrationConsiderations = () => {
  return (
    <>
      {/* Migration Considerations */}
      <h3 className="text-2xl font-bold text-gray-800 mb-6">Migration Considerations</h3>
      <p className="text-gray-700 leading-relaxed mb-6">
        If you're migrating from other AI models to ASI1, these considerations will help you leverage ASI1's unique Web3-native and agentic capabilities effectively.
      </p>
      
      <div className="mb-8">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">From General AI Models to ASI1</h4>
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-4">
          <h5 className="font-semibold text-blue-700 mb-3">Key Advantages to Leverage:</h5>
          <ul className="space-y-2 text-gray-700">
            <li>• <strong>Web3-Native Understanding:</strong> ASI1 inherently understands blockchain concepts, DeFi protocols, and decentralized systems without requiring extensive context</li>
            <li>• <strong>Agentic Reasoning:</strong> Built for autonomous decision-making and multi-step workflow execution</li>
            <li>• <strong>Financial Precision:</strong> Lower hallucination rates for financial data and trading operations</li>
            <li>• <strong>Protocol Integration:</strong> Seamless interaction with various DeFi protocols and smart contract systems</li>
          </ul>
        </div>
      </div>

      <div className="mb-8">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">Prompt Adaptation Strategies</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-red-50 p-6 rounded-lg border border-red-200">
            <h5 className="font-semibold text-red-600 mb-3">❌ Generic AI Approach:</h5>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>• Lengthy blockchain explanations</li>
              <li>• Basic DeFi protocol descriptions</li>
              <li>• Manual step-by-step guidance</li>
              <li>• Single protocol focus</li>
            </ul>
          </div>
          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h5 className="font-semibold text-green-600 mb-3">✅ ASI1 Optimized:</h5>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>• Direct protocol integration instructions</li>
              <li>• Autonomous workflow definitions</li>
              <li>• Advanced coordination logic</li>
              <li>• Agent-to-agent communication patterns</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">Enhanced Capabilities to Explore</h4>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-semibold text-purple-700 mb-3">Autonomous Operations:</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Self-executing trading strategies</li>
                <li>• Dynamic risk management</li>
                <li>• Automated yield optimization</li>
                <li>• Intelligent gas fee management</li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold text-purple-700 mb-3">Advanced Integrations:</h5>
              <ul className="space-y-1 text-gray-700 text-sm">
                <li>• Multi-protocol arbitrage</li>
                <li>• Automated portfolio rebalancing</li>
                <li>• Decentralized governance participation</li>
                <li>• Real-time market analysis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-12">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">Testing and Validation</h4>
        <p className="text-gray-700 leading-relaxed mb-4">
          When migrating to ASI1, implement these validation steps:
        </p>
        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 mb-4">
          <ul className="space-y-2 text-gray-700">
            <li>• Start with testnet environments for all blockchain operations</li>
            <li>• Implement comprehensive logging for agent decision-making</li>
            <li>• Set up monitoring for autonomous workflow execution</li>
            <li>• Create fallback mechanisms for unexpected market conditions</li>
            <li>• Establish clear success metrics for agentic behaviors</li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default MigrationConsiderations;
